import { notify } from '@/lib/toast';
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RecordLink } from '@/components/records/RecordLink';
import { Plus, Search, Hash } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ProductSearchCombobox } from '@/components/inventory/ProductSearchCombobox';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterChips } from '@/components/ui/filter-chips';
import { useTranslation } from 'react-i18next';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

type SerialStatus = 'AVAILABLE' | 'SOLD' | 'RETURNED' | 'DAMAGED';

const STATUS_LABELS: Record<SerialStatus, string> = {
  AVAILABLE: 'In stock',
  SOLD: 'Sold',
  RETURNED: 'Returned',
  DAMAGED: 'Damaged',
};

interface SerialNumber {
  id: string;
  serialNo: string;
  imei1?: string | null;
  imei2?: string | null;
  color?: string | null;
  storage?: string | null;
  warrantyMonths?: number | null;
  warrantyExpiry?: string | null;
  status: SerialStatus;
  productId: string;
  product?: { name: string; sku: string } | null;
  notes?: string | null;
  soldAt?: string | null;
  /** T-484: latest completed sale that sold this unit (derived server-side from SaleItem). */
  sale?: { id: string; invoiceNumber: string; customer?: { id: string; name: string } | null } | null;
}

const SERIAL_FILTERS = { serialTracking: 'true' };


interface CreateSerialPayload {
  productId: string;
  serialNo: string;
  imei1?: string;
  imei2?: string;
  color?: string;
  storage?: string;
  warrantyMonths?: number;
  notes?: string;
}

interface BulkSerialPayload {
  productId: string;
  serialNos: string[];
}

interface BulkSerialResult {
  created: number;
}

const STATUS_CHIPS: { key: SerialStatus | ''; label: string }[] = [
  // Chip labels stay English; only the table headers are localized (T-419).
  { key: '', label: 'All statuses' },
  ...(Object.keys(STATUS_LABELS) as SerialStatus[]).map((k) => ({ key: k, label: STATUS_LABELS[k] })),
];

const STATUS_COLORS: Record<SerialStatus, string> = {
  AVAILABLE: 'bg-success/10 text-success-text',
  SOLD:      'bg-info/10 text-info-text',
  DAMAGED:   'bg-destructive/10 text-destructive-text',
  RETURNED:  'bg-muted text-muted-foreground',
};

const formatDate = (d: string) => new Date(d).toLocaleDateString('en-PK');

export function SerialNumbersPage() {
  const { t } = useTranslation('products');
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('serial-numbers:write') || hasPermission('serial-numbers:*') || hasPermission('inventory:*');

  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SerialStatus | ''>('');
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ productId: '', serialNo: '', imei1: '', imei2: '', color: '', storage: '', warrantyMonths: '', notes: '' });
  const [bulkForm, setBulkForm] = useState({ productId: '', serialNos: '' });

  // POS-I257: serial-tracked products are searched on the server (ProductSearchCombobox), not loaded as one fixed page

  const { data, isLoading } = useQuery({
    queryKey: ['serial-numbers', debouncedSearch, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      return api.get(`/serial-numbers?${params}`).then((r) => r.data);
    },
  });

  const serials: SerialNumber[] = data?.data ?? [];

  const addMutation = useMutation({
    mutationFn: (d: CreateSerialPayload) => api.post('/serial-numbers', d).then((r) => r.data),
    onSuccess: () => {
      toast.success('Serial number added');
      queryClient.invalidateQueries({ queryKey: ['serial-numbers'] });
      setAddOpen(false);
      setForm({ productId: '', serialNo: '', imei1: '', imei2: '', color: '', storage: '', warrantyMonths: '', notes: '' });
    },
    onError: (err) => notify.error(err, 'Failed to add'),
  });

  const bulkMutation = useMutation({
    mutationFn: (d: BulkSerialPayload) => api.post<BulkSerialResult>('/serial-numbers/bulk', d).then((r) => r.data),
    onSuccess: (res: BulkSerialResult) => {
      toast.success(`Added ${res.created} serial numbers`);
      queryClient.invalidateQueries({ queryKey: ['serial-numbers'] });
      setBulkOpen(false);
      setBulkForm({ productId: '', serialNos: '' });
    },
    onError: (err) => notify.error(err, 'Failed to add'),
  });

  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleSearch = (val: string) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(val), 400);
  };

  const handleAdd = () => {
    if (!form.productId || !form.serialNo) { toast.error('Product and serial number are required'); return; }
    addMutation.mutate({
      productId: form.productId,
      serialNo: form.serialNo,
      imei1: form.imei1 || undefined,
      imei2: form.imei2 || undefined,
      color: form.color || undefined,
      storage: form.storage || undefined,
      warrantyMonths: form.warrantyMonths ? Number(form.warrantyMonths) : undefined,
      notes: form.notes || undefined,
    });
  };

  const handleBulk = () => {
    if (!bulkForm.productId || !bulkForm.serialNos.trim()) { toast.error('Product and serial numbers are required'); return; }
    const serialNos = bulkForm.serialNos.split('\n').map((s) => s.trim()).filter(Boolean);
    if (serialNos.length === 0) { toast.error('Enter at least one serial number'); return; }
    bulkMutation.mutate({ productId: bulkForm.productId, serialNos });
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Serial Numbers & IMEI"
        subtitle="Track individual units by serial number or IMEI"
        actions={canWrite ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="gap-2">
              <Hash className="h-4 w-4" /> Bulk Add
            </Button>
            <Button onClick={() => setAddOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Add Serial
            </Button>
          </div>
        ) : undefined}
      />

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="ps-9"
            placeholder="Search serial, IMEI…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <FilterChips
          chips={STATUS_CHIPS}
          value={statusFilter}
          onChange={(k) => setStatusFilter(k as SerialStatus | '')}
          variant="outline"
          size="sm"
          wrap
        />
        <p className="text-sm text-muted-foreground ms-auto">{data?.total ?? 0} total</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <SkeletonTable rows={5} cols={5} />
          ) : serials.length === 0 ? (
            <EmptyState
              icon={Hash}
              title="No serial numbers found"
              subtitle={canWrite && !debouncedSearch ? 'Add your first serial number to start tracking.' : undefined}
              action={canWrite && !debouncedSearch ? { label: 'Add Serial Number', onClick: () => setAddOpen(true) } : undefined}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tbl.serialImei')}</Th>
                    <Th>{t('productColumn')}</Th>
                    <Th>{t('tbl.status')}</Th>
                    <Th>{t('tbl.warrantyUntil')}</Th>
                    <Th>{t('tbl.soldAt')}</Th>
                    <Th>{t('tbl.invoice')}</Th>
                    <Th>{t('tbl.customer')}</Th>
                    <Th>{t('tbl.notes')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {serials.map((s) => (
                    <Tr key={s.id}>
                      <Td label={t('tbl.serialImei')}>
                        <p className="font-mono font-medium">{s.serialNo}</p>
                        {(s.imei1 || s.imei2) && (
                          <p className="text-xs text-muted-foreground font-mono">
                            {s.imei1 ?? '—'}{s.imei2 ? ` / ${s.imei2}` : ''}
                          </p>
                        )}
                        {(s.color || s.storage) && (
                          <p className="text-xs text-muted-foreground">{[s.color, s.storage].filter(Boolean).join(' · ')}</p>
                        )}
                      </Td>
                      <Td label={t('productColumn')}>
                        {s.product ? (
                          <div>
                            <p className="font-medium">{s.product.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{s.product.sku}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">{s.productId}</span>
                        )}
                      </Td>
                      <Td label={t('tbl.status')}>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status]}`}>
                          {STATUS_LABELS[s.status] ?? s.status}
                        </span>
                      </Td>
                      <Td label={t('tbl.warrantyUntil')} className="text-muted-foreground text-xs">
                        {s.warrantyExpiry ? formatDate(s.warrantyExpiry) : s.warrantyMonths ? `${s.warrantyMonths} mo` : '—'}
                      </Td>
                      <Td label={t('tbl.soldAt')} className="text-muted-foreground text-xs">
                        {s.soldAt ? formatDate(s.soldAt) : '—'}
                      </Td>
                      <Td label={t('tbl.invoice')} className="text-xs">
                        {s.sale && s.status === 'SOLD' ? (
                          <RecordLink type="invoice" id={s.sale.id} label={s.sale.invoiceNumber} variant="mono" className="text-xs" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td label={t('tbl.customer')} className="text-muted-foreground text-xs">
                        {s.status === 'SOLD' ? s.sale?.customer?.name ?? '—' : '—'}
                      </Td>
                      <Td label={t('tbl.notes')} className="text-muted-foreground text-xs">{s.notes ?? '—'}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Single Dialog */}
      <Dialog
        visible={addOpen}
        onHide={() => setAddOpen(false)}
        header="Add Serial Number"
        modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '50vw' }}
      >
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Product *</Label>
            <ProductSearchCombobox
              value={form.productId}
              onChange={(val) => setForm({ ...form, productId: val })}
              filters={SERIAL_FILTERS}
              placeholder="Search products…"
              searchPlaceholder="Search by name, SKU or barcode…"
              emptyText="No serial-tracked products found. Enable serial tracking on products first."
              />
          </div>
          <div>
            <Label className="text-sm font-medium">Serial Number / IMEI *</Label>
            <Input 
              className="mt-1"
              value={form.serialNo} 
              onChange={(e) => setForm({ ...form, serialNo: e.target.value })} 
              placeholder="SN-12345678 or 357013090378671" 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">IMEI 1</Label>
              <Input
                className="mt-1"
                value={form.imei1}
                onChange={(e) => setForm({ ...form, imei1: e.target.value })}
                placeholder="Primary IMEI"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IMEI 2</Label>
              <Input
                className="mt-1"
                value={form.imei2}
                onChange={(e) => setForm({ ...form, imei2: e.target.value })}
                placeholder="Secondary IMEI (dual-SIM)"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Color</Label>
              <Input
                className="mt-1"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="e.g. Midnight Black"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Storage Variant</Label>
              <Input
                className="mt-1"
                value={form.storage}
                onChange={(e) => setForm({ ...form, storage: e.target.value })}
                placeholder="e.g. 128GB"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Warranty (months)</Label>
              <Input
                className="mt-1"
                type="number"
                min={0}
                value={form.warrantyMonths}
                onChange={(e) => setForm({ ...form, warrantyMonths: e.target.value })}
                placeholder="e.g. 12"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Notes</Label>
            <Input 
              className="mt-1"
              value={form.notes} 
              onChange={(e) => setForm({ ...form, notes: e.target.value })} 
              placeholder="Optional notes" 
            />
          </div>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addMutation.isPending}>
              {addMutation.isPending ? 'Adding…' : 'Add Serial'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Bulk Add Dialog */}
      <Dialog
        visible={bulkOpen}
        onHide={() => setBulkOpen(false)}
        header="Bulk Add Serial Numbers"
        modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '50vw' }}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Enter one serial number per line.</p>
          <div>
            <Label className="text-sm font-medium">Product *</Label>
            <ProductSearchCombobox
              value={bulkForm.productId}
              onChange={(val) => setBulkForm({ ...bulkForm, productId: val })}
              filters={SERIAL_FILTERS}
              placeholder="Search products…"
              searchPlaceholder="Search by name, SKU or barcode…"
              emptyText="No serial-tracked products found. Enable serial tracking on products first."
              />
          </div>
          <div>
            <Label className="text-sm font-medium">Serial Numbers (one per line) *</Label>
            <Textarea
              className="font-mono min-h-[120px] resize-none mt-1"
              placeholder={"SN-00001\nSN-00002\nSN-00003"}
              value={bulkForm.serialNos}
              onChange={(e) => setBulkForm({ ...bulkForm, serialNos: e.target.value })}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {bulkForm.serialNos.split('\n').filter((s) => s.trim()).length} serials entered
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={handleBulk} disabled={bulkMutation.isPending}>
              {bulkMutation.isPending ? 'Adding…' : 'Add All'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
